import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

import { callAll } from "../../utils/call-all";
import { useStableRest } from "../../utils/use-stable-rest";

import {
  SCROLL_STEP_PX,
  keyTarget,
  measureScrollBar,
  readAxis,
  scrollAxisTo,
} from "./scroll-bar.math";
import type { ScrollBarMeasure } from "./scroll-bar.math";
import type {
  ScrollBarCaretDirection,
  ScrollBarOrientation,
  ScrollBarRootAttributes,
  ScrollBarViewModel,
  UseScrollBarOptions,
} from "./types-scroll-bar";

const CARETS: Record<ScrollBarOrientation, ScrollBarCaretDirection[]> = {
  vertical: ["up", "down"],
  horizontal: ["left", "right"],
};

const STEP_BY_DIRECTION: Record<ScrollBarCaretDirection, number> = {
  up: -SCROLL_STEP_PX,
  left: -SCROLL_STEP_PX,
  down: SCROLL_STEP_PX,
  right: SCROLL_STEP_PX,
};

function paintThumb(
  thumb: HTMLElement | null,
  orientation: ScrollBarOrientation,
  { thumbSize, thumbOffset }: ScrollBarMeasure,
) {
  if (!thumb) return;
  if (orientation === "vertical") {
    thumb.style.height = `${thumbSize}px`;
    thumb.style.transform = `translateY(${thumbOffset}px)`;
  } else {
    thumb.style.width = `${thumbSize}px`;
    thumb.style.transform = `translateX(${thumbOffset}px)`;
  }
}

/** Barra de rolagem do `scrollerRef`: só estouro e arraste são estado; o polegar e o `aria-valuenow` são pintados por ref. */
export function useScrollBar({
  scrollerRef,
  orientation = "vertical",
  inset = 0,
  controls,
  testId,
  className,
  style,
  onKeyDown,
  ...props
}: UseScrollBarOptions & ScrollBarRootAttributes): ScrollBarViewModel {
  const rest = useStableRest(props);
  const vertical = orientation === "vertical";
  const generatedId = useId();
  const [overflowing, setOverflowing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [scrollerId, setScrollerId] = useState<string | undefined>(undefined);

  const root = useRef<HTMLDivElement | null>(null);
  const rail = useRef<HTMLDivElement | null>(null);
  const thumb = useRef<HTMLDivElement | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const frame = useRef(0);
  const overflowingRef = useRef(false);
  const measured = useRef<ScrollBarMeasure | null>(null);
  const drag = useRef<{ pointer: number; scrolled: number } | null>(null);

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const railElement = rail.current;
    const railLength = railElement
      ? vertical
        ? railElement.clientHeight
        : railElement.clientWidth
      : 0;
    const result = measureScrollBar(scroller, railLength, orientation, inset);
    measured.current = result;
    if (result.overflowing !== overflowingRef.current) {
      overflowingRef.current = result.overflowing;
      setOverflowing(result.overflowing);
    }
    paintThumb(thumb.current, orientation, result);
    root.current?.setAttribute("aria-valuenow", String(result.percent));
  }, [scrollerRef, orientation, vertical, inset]);

  /** Uma medida por frame, não importa quantos eventos cheguem. */
  const requestMeasure = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      measure();
    });
  }, [measure]);

  // Síncrono: a barra nasce já medida.
  useLayoutEffect(() => {
    measure();
  }, [measure, overflowing]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.addEventListener("scroll", requestMeasure, { passive: true });
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(requestMeasure);
    observer.current = resizeObserver;
    resizeObserver?.observe(scroller);
    for (const child of scroller.children) resizeObserver?.observe(child);
    if (root.current) resizeObserver?.observe(root.current);
    // Filhos que chegam depois (tabela que remonta) também mudam o estouro.
    const mutationObserver = new MutationObserver((records) => {
      for (const { removedNodes } of records) {
        for (const node of removedNodes) {
          if (node instanceof Element) resizeObserver?.unobserve(node);
        }
      }
      for (const child of scroller.children) resizeObserver?.observe(child);
      requestMeasure();
    });
    mutationObserver.observe(scroller, { childList: true });
    return () => {
      scroller.removeEventListener("scroll", requestMeasure);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      observer.current = null;
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
  }, [scrollerRef, requestMeasure]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || controls) return;
    if (!scroller.id) scroller.id = generatedId;
    setScrollerId(scroller.id);
  }, [scrollerRef, controls, generatedId]);

  const rootRef = useCallback((element: HTMLDivElement | null) => {
    if (root.current) observer.current?.unobserve(root.current);
    root.current = element;
    if (element) observer.current?.observe(element);
  }, []);

  const railRef = useCallback((element: HTMLDivElement | null) => {
    rail.current = element;
  }, []);

  const thumbRef = useCallback((element: HTMLDivElement | null) => {
    thumb.current = element;
  }, []);

  const scrollBy = useCallback(
    (delta: number) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      scrollAxisTo(scroller, orientation, readAxis(scroller, orientation).scrolled + delta);
    },
    [scrollerRef, orientation],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const { scrolled, viewport, scrollable } = readAxis(scroller, orientation, inset);
      const target = keyTarget(event.key, orientation, scrolled, viewport, scrollable);
      if (target === undefined) return;
      event.preventDefault();
      scrollAxisTo(scroller, orientation, target);
    },
    [scrollerRef, orientation, inset],
  );

  const onTrackPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const scroller = scrollerRef.current;
      const thumbElement = thumb.current;
      if (!scroller || !thumbElement) return;
      const box = thumbElement.getBoundingClientRect();
      const point = vertical ? event.clientY : event.clientX;
      const { viewport } = readAxis(scroller, orientation, inset);
      if (point < (vertical ? box.top : box.left)) scrollBy(-viewport);
      else if (point > (vertical ? box.bottom : box.right)) scrollBy(viewport);
    },
    [scrollerRef, orientation, inset, vertical, scrollBy],
  );

  const thumbHandlers = useMemo(
    () => ({
      onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        drag.current = {
          pointer: vertical ? event.clientY : event.clientX,
          scrolled: readAxis(scroller, orientation).scrolled,
        };
        setDragging(true);
      },
      onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
        const scroller = scrollerRef.current;
        const current = drag.current;
        const result = measured.current;
        if (!scroller || !current || !result || result.travel <= 0) return;
        const delta = (vertical ? event.clientY : event.clientX) - current.pointer;
        scrollAxisTo(
          scroller,
          orientation,
          current.scrolled + (delta / result.travel) * result.scrollable,
        );
      },
      onPointerUp: () => {
        drag.current = null;
        setDragging(false);
      },
      onPointerCancel: () => {
        drag.current = null;
        setDragging(false);
      },
    }),
    [scrollerRef, orientation, vertical],
  );

  const stepTo = useCallback(
    (direction: ScrollBarCaretDirection) => scrollBy(STEP_BY_DIRECTION[direction]),
    [scrollBy],
  );

  return useMemo(
    () => ({
      visible: overflowing,
      dragging,
      orientation,
      controls: controls ?? scrollerId,
      testId,
      className,
      style,
      rest,
      carets: CARETS[orientation],
      rootRef,
      railRef,
      thumbRef,
      onKeyDown: callAll(handleKeyDown, onKeyDown),
      onTrackPointerDown,
      thumbHandlers,
      stepTo,
      partTestId: (part: string, suffix?: string) =>
        testId ? [testId, part, suffix].filter(Boolean).join("-") : undefined,
    }),
    [
      overflowing,
      dragging,
      orientation,
      controls,
      scrollerId,
      testId,
      className,
      style,
      rest,
      rootRef,
      railRef,
      thumbRef,
      handleKeyDown,
      onKeyDown,
      onTrackPointerDown,
      thumbHandlers,
      stepTo,
    ],
  );
}
